import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, CreditCard, Tag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { LEGAL } from "@/lib/brand";
import { toast } from "sonner";

export default function Cart() {
  const { items, removeItem, setQty, subtotal, discount, total, promo, setPromo, clearPromo } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const ref = localStorage.getItem("ape_ref");
    if (ref && !promo && items.length > 0) {
      api.post("/promo/validate", { code: ref })
        .then(({ data }) => { setPromo({ code: data.code, percent_off: data.percent_off }); toast.success(`Referral code ${data.code} applied — ${data.percent_off}% off!`); })
        .catch(() => localStorage.removeItem("ape_ref"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promoInput });
      setPromo({ code: data.code, percent_off: data.percent_off });
      setPromoInput("");
      toast.success(`Code ${data.code} applied — ${data.percent_off}% off!`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Invalid promo code");
    } finally { setApplying(false); }
  };

  const stripeCheckout = async () => {
    if (!user) { toast.error("Please log in to check out."); nav("/login"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/cart-checkout", {
        items: items.map((i) => ({ slug: i.slug, name: i.name, price: i.price, qty: i.qty })),
        origin_url: window.location.origin,
        promo_code: promo?.code || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <ShoppingBag className="mx-auto text-ape-red mb-6" size={56} />
        <h1 className="font-display text-4xl tracking-tight">YOUR CART IS EMPTY</h1>
        <p className="text-ape-muted mt-3">Time to fuel the beast. Browse the arsenal and evolve beyond limits.</p>
        <Link to="/shop" data-testid="cart-shop-btn" className="inline-flex mt-8 bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest px-8 py-4 transition-colors duration-150">Shop Peptides</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
      <h1 className="font-display text-5xl tracking-tight mb-10">YOUR CART</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((i) => (
            <div key={i.slug} className="flex items-center gap-4 border border-ape-border bg-ape-surface p-4" data-testid={`cart-item-${i.slug}`}>
              <div className="w-14 h-14 shrink-0 flex items-center justify-center border border-ape-border" style={{ backgroundColor: `${i.accent}22` }}>
                <span className="font-display text-lg" style={{ color: i.accent }}>{i.name.slice(0, 3)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-head uppercase tracking-wide">{i.name}</div>
                <div className="font-mono2 text-xs text-ape-muted">{i.dosage} · ${i.price.toFixed(2)}</div>
              </div>
              <div className="flex items-center border border-ape-border">
                <button onClick={() => setQty(i.slug, i.qty - 1)} className="p-2 hover:text-ape-red transition-colors duration-150" data-testid={`cart-dec-${i.slug}`}><Minus size={14} /></button>
                <span className="w-10 text-center font-mono2">{i.qty}</span>
                <button onClick={() => setQty(i.slug, i.qty + 1)} className="p-2 hover:text-ape-red transition-colors duration-150" data-testid={`cart-inc-${i.slug}`}><Plus size={14} /></button>
              </div>
              <div className="font-mono2 font-bold w-20 text-right">${(i.price * i.qty).toFixed(2)}</div>
              <button onClick={() => removeItem(i.slug)} className="text-ape-muted hover:text-ape-red transition-colors duration-150" data-testid={`cart-remove-${i.slug}`}><Trash2 size={18} /></button>
            </div>
          ))}
        </div>

        <div className="border border-ape-border bg-ape-surface p-6 h-fit">
          <h2 className="font-display text-2xl tracking-wide mb-5">ORDER SUMMARY</h2>

          {/* Promo code */}
          <div className="mb-5" data-testid="promo-section">
            <div className="flex items-center gap-2 font-mono2 text-[10px] tracking-widest text-ape-muted mb-2"><Tag size={12} className="text-ape-red" /> PROMO CODE</div>
            {promo ? (
              <div className="flex items-center justify-between border border-ape-red bg-ape-red/10 px-3 py-2.5" data-testid="promo-applied">
                <span className="font-mono2 text-sm text-ape-red">{promo.code} · {promo.percent_off}% OFF</span>
                <button onClick={() => { clearPromo(); toast.message("Promo removed"); }} className="text-ape-muted hover:text-ape-red transition-colors duration-150" data-testid="promo-remove"><X size={16} /></button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && applyPromo()} placeholder="Enter code" data-testid="promo-input"
                  className="flex-1 bg-transparent border-2 border-ape-border rounded-none px-3 py-2 text-white font-mono2 text-sm uppercase focus:border-ape-red focus:outline-none transition-colors duration-150" />
                <button onClick={applyPromo} disabled={applying} data-testid="promo-apply"
                  className="bg-ape-red hover:bg-ape-redhover disabled:opacity-50 text-white font-head uppercase tracking-widest text-sm px-4 transition-colors duration-150">
                  {applying ? "…" : "Apply"}
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-between text-sm mb-2"><span className="text-ape-muted">Subtotal</span><span className="font-mono2" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
          {promo && (
            <div className="flex justify-between text-sm mb-2 text-ape-red" data-testid="cart-discount"><span>Discount ({promo.percent_off}%)</span><span className="font-mono2">−${discount.toFixed(2)}</span></div>
          )}
          <div className="flex justify-between text-sm mb-2"><span className="text-ape-muted">Shipping</span><span className="font-mono2">Calculated at checkout</span></div>
          <div className="border-t border-ape-border my-4" />
          <div className="flex justify-between items-baseline mb-6"><span className="font-head uppercase tracking-wide">Total</span><span className="font-mono2 text-2xl font-bold text-ape-red" data-testid="cart-total">${total.toFixed(2)}</span></div>
          <button onClick={stripeCheckout} disabled={loading} data-testid="cart-stripe-checkout" className="w-full bg-ape-red hover:bg-ape-redhover disabled:opacity-50 text-white font-head uppercase tracking-widest py-4 flex items-center justify-center gap-2 transition-colors duration-150">
            <CreditCard size={18} /> {loading ? "Redirecting…" : "Secure Checkout"}
          </button>
          <p className="text-ape-muted text-xs text-center mt-3 font-mono2">Shipping & payment collected securely on the next step.</p>
          <div className="mt-4 font-mono2 text-[10px] tracking-widest text-ape-muted text-center">{LEGAL}</div>
        </div>
      </div>
    </div>
  );
}
